import {StoppageDto as Stoppage, StoppageKind} from '@hconnect/common/types'
import {Iso8601} from '@hconnect/uikit'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CloseIcon from '@mui/icons-material/Close'
import ContentCutIcon from '@mui/icons-material/ContentCut'
import {Button, DialogActions, DialogContent, Typography, Alert, Stack} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import {COMMON_SHIFT_EVENT_WIDGET_NAMESPACE} from '@widgets/common-shift-event'
import {isNumber} from 'lodash'
import moment, {Moment} from 'moment-timezone'
import {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_RUNNING_TIMES_WIDGET_NAMESPACE} from '../consts'
import {getStoppageSplitInitialData} from '../utils'

import {SliderThumbBox, StoppageSlider} from './StoppageSlider'
import {StoppageSplitDateTimeEditor} from './StoppageSplitDateTimeEditor'
import {StoppageSplitDurationDetails} from './StoppageSplitDurationDetails'

type SplitStoppageDialogProps = {
  timezone: string
  stoppage: Stoppage
  open: boolean
  onClose: (submit?: boolean) => void
  onSplitSubmit: (initialStoppageData: Partial<Stoppage>) => void
  pxTrendAvailable?: boolean
}

export const SplitStoppageDialog: React.FC<SplitStoppageDialogProps> = ({
  timezone,
  stoppage,
  open,
  onClose,
  onSplitSubmit,
  pxTrendAvailable = false
}) => {
  const {t} = useTranslation(COMMON_RUNNING_TIMES_WIDGET_NAMESPACE)
  const {t: commonShiftEventTranslate} = useTranslation(COMMON_SHIFT_EVENT_WIDGET_NAMESPACE)
  const isPlannedStoppage = stoppage.eventType === StoppageKind.PlannedStoppage
  const stoppageStart = moment.utc(stoppage.stoppageStart).tz(timezone)
  const stoppageEnd = moment.utc(stoppage.stoppageEnd).tz(timezone)
  const duration = moment.duration(stoppageEnd.diff(stoppageStart))
  const totalMinutes = duration.asMinutes()

  const defaultSplitValueInMins = Math.ceil(totalMinutes / 2)
  const [splitValueInMins, _setSplitValueInMins] = useState<number>(defaultSplitValueInMins)
  const [splitDate, _setSplitDate] = useState<Moment>(
    stoppageStart.clone().add(defaultSplitValueInMins, 'minutes')
  )

  const setSplitValueInMins = useCallback(
    (value: number) => {
      _setSplitValueInMins(value)
      const splitTime = stoppageStart.clone().add(value, 'minutes')
      _setSplitDate(splitTime)
    },
    [stoppageStart]
  )

  const setSplitDate = useCallback(
    (date: Moment) => {
      const value = date.diff(stoppageStart, 'minutes')
      _setSplitValueInMins(value)
      _setSplitDate(date)
    },
    [stoppageStart]
  )

  const stoppageTimeRangeUtc: [Iso8601, Iso8601 | undefined] = [
    stoppage.stoppageStart,
    stoppage.stoppageEnd
  ]

  const stoppageLabel = `${stoppage.equipment?.text || ''} ${commonShiftEventTranslate(
    `shiftEvent.stoppage.${stoppage.eventType}`
  )}`

  return (
    <Dialog open={open} onClose={() => onClose()} PaperProps={{sx: {width: '514px'}}}>
      <DialogTitle>
        <Typography
          onClick={() => onClose()}
          sx={{cursor: 'pointer'}}
          variant="subtitle2"
          color="primary"
          display="flex"
          alignItems="center"
          component="span"
          width="fit-content"
        >
          <ChevronLeftIcon />
          {t('splitStoppage.action.backToOriginal', {
            originalStoppage: stoppageLabel
          })}
        </Typography>
        <Typography variant="h3" component="span">
          {t('splitStoppage.title')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4} alignItems={'center'}>
          <StoppageSplitDateTimeEditor
            stoppageTimeRange={[stoppageStart, stoppageEnd]}
            timezone={timezone}
            splitDate={splitDate}
            setSplitDate={setSplitDate}
          />
          {pxTrendAvailable && (
            <StoppageSlider
              value={splitValueInMins}
              onChange={(_, value: number | number[]) => {
                isNumber(value) && setSplitValueInMins(value)
              }}
              min={0}
              max={totalMinutes}
              // valueLabelDisplay="auto" // Add back with TODO in ./StoppageSlider.tsx
              slotProps={
                {
                  // valueLabel: { // Add back with  TODO in ./StoppageSlider.tsx
                  //   stoppage
                  // }
                }
              }
              slots={{thumb: SliderThumbBox}} // valueLabel: SliderValueLabel, Add back with  TODO in ./StoppageSlider.tsx
              isPlannedStoppage={isPlannedStoppage}
              data-test-id="stoppage-split-slider"
            />
          )}
          <StoppageSplitDurationDetails
            stoppageTimeRangeUtc={stoppageTimeRangeUtc}
            splitDateUtc={splitDate.toISOString()}
            timezone={timezone}
          />
          <Alert color="info" icon={false}>
            {t('splitStoppage.message.editableAfterSplitHint')}
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} variant="text" startIcon={<CloseIcon />}>
          {t('splitStoppage.action.cancel')}
        </Button>
        <Button
          onClick={() => {
            const initialStoppageData: Partial<Stoppage> = getStoppageSplitInitialData(
              stoppage,
              splitDate.toISOString()
            )
            onSplitSubmit(initialStoppageData)
            onClose(true)
          }}
          color="primary"
          variant="contained"
          disabled={splitValueInMins <= 0 || splitValueInMins >= totalMinutes}
          startIcon={<ContentCutIcon />}
          data-test-id="split-stoppage-button"
        >
          {t('splitStoppage.action.split')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
